import { useCallback, useReducer, useRef, useMemo } from 'react';
import { getNewUTCDate } from 'utils/time';
import { FEATURES } from 'lib/services/copilot';
import { getAgents } from 'lib/services/copilotAgents';
import { useWeb5Context } from 'lib/Web5Context';
import { errorToast } from 'utils/toast';
import { COPILOT_BAR_LOADING_STATES } from './constants';

/**
 * Default properties for a chat interaction
 * @property {string} content - The chat message content
 * @property {string} role - The role of the interaction ('user' or 'assistant')
 * @property {string} vote - Vote status for the interaction
 * @property {boolean} isLoading - Whether the interaction is currently loading
 * @property {boolean} isFinished - Whether the interaction is complete
 * @property {boolean} isError - Whether an error occurred during the interaction
 * @property {string|null} parentId - ID of the parent interaction if this is a response
 * @property {Array} actions - Array of action objects associated with the interaction
 * @property {Array} forms - Array of form objects associated with the interaction
 * @property {boolean} disableVote - Whether voting is disabled for this interaction
 */
const defaultChat = {
    content: '',
    role: '',
    vote: '',
    isLoading: false,
    isFinished: false,
    isError: false,
    parentId: null,
    actions: [],
    forms: [],
    artifacts: [],
    disableVote: false,
};

/**
 * Default properties for a single chat session
 * @property {Object} chat - Map of chat interactions indexed by interaction ID
 * @property {Object} chatSession - Session data for the chat
 * @property {boolean} isInitialized - Whether the chat session has been initialized
 * @property {Object|null} activeAgent - The currently active copilot agent for this chat
 * @property {Object} reports - Reports data associated with the chat session
 * @property {Object} tables - Table data associated with the chat session
 */
const defaultChatState = {
    chat: {},
    chatSession: {},
    isInitialized: false,
    activeAgent: null,
    reports: {},
    tables: {},
    artifacts: [],
};

/**
 * Initial state for the copilot reducer
 * @property {Object} chats - Map of chat sessions indexed by chatId
 * @property {Array} copilotAgents - List of available copilot agents
 * @property {Object} goldenMinute - Golden minute feature state with CTA view tracking
 * @property {Object} copilotBarProps - State properties for the CopilotBar component
 */
const initialState = {
    chats: {},
    copilotAgents: [],
    goldenMinute: { ctaViews: 0 },
    copilotBarProps: {
        chatSetup: null,
        doneMessage: null,
        feedback: null,
        files: [],
        height: 0,
        ignoreCopilotSidebarInset: false,
        initialLoadingPhrase: null,
        inputMode: null,
        inputPlaceholder: null,
        isOpen: false,
        isMinimized: false,
        loadingPhrase: '',
        loadingState: COPILOT_BAR_LOADING_STATES.IDLE,
        padding: 24,
        pinnedToRight: false,
        showInputModeControls: true,
        transition: 'fade',
        uploadingFiles: false,
        width: '100%',
    },
};

/**
 * Action types for the copilot reducer
 * @property {string} SET_INTERACTION - Update or create a chat interaction
 * @property {string} CLEAR_CHAT - Clear all chat interactions for a specific chatId
 * @property {string} CLEAR_ALL_CHATS - Clear all chat interactions
 * @property {string} SET_CHAT_SESSION - Set the current chat session for a specific chatId
 * @property {string} SET_INITIALIZED - Set initialization status for a specific chatId
 * @property {string} SET_COPILOT_AGENTS - Set available copilot agents
 * @property {string} SET_ACTIVE_AGENT - Set the active copilot agent for a specific chatId
 * @property {string} SET_VOTE - Set the vote for a specific interaction
 * @property {string} DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS - Increment the golden minute CTA views counter
 * @property {string} REMOVE_INTERACTION - Remove a single interaction from a chat
 * @property {string} SET_COPILOT_BAR_PROPS - Update CopilotBar component state properties
 */
const ACTIONS = {
    SET_INTERACTION: 'SET_INTERACTION',
    REMOVE_INTERACTION: 'REMOVE_INTERACTION',
    CLEAR_CHAT: 'CLEAR_CHAT',
    CLEAR_ALL_CHATS: 'CLEAR_ALL_CHATS',
    SET_CHAT_SESSION: 'SET_CHAT_SESSION',
    SET_INITIALIZED: 'SET_INITIALIZED',
    SET_COPILOT_AGENTS: 'SET_COPILOT_AGENTS',
    SET_ACTIVE_AGENT: 'SET_ACTIVE_AGENT',
    SET_VOTE: 'SET_VOTE',
    DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS: 'DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS',
    SET_COPILOT_BAR_PROPS: 'SET_COPILOT_BAR_PROPS',
};

/**
 * Builds a stable dedupe key for a Copilot artifact.
 * Falls back to `name` when the download descriptor is incomplete.
 *
 * @param {Object} artifact - Artifact to derive the key from
 * @returns {string} Dedupe key for the artifact
 */
const getArtifactKey = (artifact) => {
    if (!artifact) return '';
    const bucket = artifact.download?.bucket;
    const name = artifact.download?.name || artifact.name;
    if (bucket && name) return `${bucket}/${name}`;
    return name || '';
};

/**
 * Merges incoming artifacts into an existing list, deduplicating by
 * `bucket/name` (or `name` as fallback). Preserves order: existing entries
 * come first, then any new entries in the order they were received.
 *
 * @param {Array} existing - Current artifact list
 * @param {Array} incoming - Artifacts to merge in
 * @returns {Array} Merged, deduplicated artifact list
 */
const mergeArtifacts = (existing = [], incoming = []) => {
    if (!incoming?.length) return existing;
    const seen = new Set(existing.map(getArtifactKey).filter(Boolean));
    const merged = [...existing];
    incoming.forEach((artifact) => {
        const key = getArtifactKey(artifact);
        if (!key || seen.has(key)) return;
        seen.add(key);
        merged.push(artifact);
    });
    return merged;
};

/**
 * Reducer function for managing copilot state
 * @param {Object} state - Current state of the copilot
 * @param {Object} action - Action object with type and payload
 * @param {string} action.type - The type of action to perform
 * @param {Object} action.payload - The data payload for the action
 * @returns {Object} Updated state after applying the action
 */
const copilotReducer = (state, action) => {
    switch (action.type) {
        case ACTIONS.SET_INTERACTION: {
            const {
                action: actionProp,
                artifact: artifactProp,
                artifacts: artifactsProp,
                audioBatches,
                audioData,
                chatId,
                creationDate,
                delta,
                disableVote,
                form: formProp,
                id,
                isError,
                isFinished,
                isLoading,
                isQuestion,
                parentId,
                report: reportProp,
                responseId,
                table: tableProp,
                text,
            } = action.payload;
            if (!id || !chatId) return state;

            // Initialize chat if it doesn't exist
            const currentChat = state.chats[chatId] || { ...defaultChatState };

            const interaction = currentChat.chat[id] || {
                creationDate: creationDate || getNewUTCDate(),
                ...defaultChat,
            };

            const updatedInteraction = { ...interaction };

            if (isError) {
                updatedInteraction.isError = true;
                updatedInteraction.isFinished = true;
                updatedInteraction.isLoading = false;
            } else {
                if (text) updatedInteraction.content = text;
                if (delta !== undefined) updatedInteraction.delta = delta;
                if (typeof isLoading === 'boolean') updatedInteraction.isLoading = isLoading;
                if (isFinished) updatedInteraction.isFinished = isFinished;
                if (responseId) updatedInteraction.responseId = responseId;
                if (parentId) updatedInteraction.parentId = parentId;
                if (typeof disableVote === 'boolean') updatedInteraction.disableVote = disableVote;
                if (audioBatches) updatedInteraction.audioBatches = audioBatches;
                if (audioData) updatedInteraction.audioData = audioData;
                if (actionProp)
                    updatedInteraction.actions = [...updatedInteraction.actions, actionProp];
                if (formProp) updatedInteraction.forms = [...updatedInteraction.forms, formProp];
                if (artifactProp) {
                    updatedInteraction.artifacts = mergeArtifacts(updatedInteraction.artifacts, [
                        artifactProp,
                    ]);
                }
                if (artifactsProp?.length) {
                    updatedInteraction.artifacts = mergeArtifacts(
                        updatedInteraction.artifacts,
                        artifactsProp,
                    );
                }
            }

            updatedInteraction.role = isQuestion ? 'user' : 'assistant';

            // Update tables and reports at the chat level
            const updatedChatState = { ...currentChat };
            if (tableProp) {
                updatedChatState.tables = { ...currentChat.tables, ...tableProp };
            }
            if (reportProp) {
                updatedChatState.reports = { ...currentChat.reports, ...reportProp };
            }
            if (artifactProp) {
                updatedChatState.artifacts = mergeArtifacts(currentChat.artifacts, [artifactProp]);
            }
            if (artifactsProp?.length) {
                updatedChatState.artifacts = mergeArtifacts(
                    updatedChatState.artifacts ?? currentChat.artifacts,
                    artifactsProp,
                );
            }

            const newState = {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...updatedChatState,
                        chat: { ...currentChat.chat, [id]: updatedInteraction },
                    },
                },
            };

            return newState;
        }
        case ACTIONS.REMOVE_INTERACTION: {
            const { chatId, id } = action.payload;
            if (!chatId || !id) return state;

            const currentChat = state.chats[chatId];
            if (!currentChat?.chat?.[id]) return state;

            const { [id]: _removed, ...remainingChat } = currentChat.chat;
            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...currentChat,
                        chat: remainingChat,
                    },
                },
            };
        }
        case ACTIONS.CLEAR_CHAT: {
            const { chatId } = action.payload;
            if (!chatId) return state;

            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...defaultChatState,
                    },
                },
            };
        }
        case ACTIONS.CLEAR_ALL_CHATS:
            return {
                ...state,
                chats: {},
            };
        case ACTIONS.SET_CHAT_SESSION: {
            const { chatId, session } = action.payload;
            if (!chatId) return state;

            const currentChat = state.chats[chatId] || { ...defaultChatState };

            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...currentChat,
                        chatSession: session,
                    },
                },
            };
        }
        case ACTIONS.SET_INITIALIZED: {
            const { chatId, value } = action.payload;
            if (!chatId) return state;

            const currentChat = state.chats[chatId] || { ...defaultChatState };

            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...currentChat,
                        isInitialized: value,
                    },
                },
            };
        }
        case ACTIONS.SET_COPILOT_AGENTS:
            return {
                ...state,
                copilotAgents: action.payload,
            };
        case ACTIONS.SET_ACTIVE_AGENT: {
            const { chatId, agent } = action.payload;
            if (!chatId) return state;

            const currentChat = state.chats[chatId] || { ...defaultChatState };

            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...currentChat,
                        activeAgent: agent,
                    },
                },
            };
        }
        case ACTIONS.SET_VOTE: {
            const { chatId, interactionId, vote } = action.payload;
            if (!chatId || !interactionId) return state;

            const currentChat = state.chats[chatId] || { ...defaultChatState };
            const interaction = currentChat.chat[interactionId];

            if (!interaction) return state;

            return {
                ...state,
                chats: {
                    ...state.chats,
                    [chatId]: {
                        ...currentChat,
                        chat: {
                            ...currentChat.chat,
                            [interactionId]: {
                                ...interaction,
                                vote: vote,
                            },
                        },
                    },
                },
            };
        }
        case ACTIONS.DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS: {
            return {
                ...state,
                goldenMinute: {
                    ctaViews: state.goldenMinute?.ctaViews + 1,
                },
            };
        }
        case ACTIONS.SET_COPILOT_BAR_PROPS: {
            return {
                ...state,
                copilotBarProps: {
                    ...state.copilotBarProps,
                    ...action.payload,
                },
            };
        }
        default:
            return state;
    }
};

/**
 * Custom hook for managing copilot chat interactions, agent selection, and streaming operations
 *
 * This hook provides a comprehensive interface for managing copilot functionality including:
 * - Chat interaction management (create, update, clear) for multiple concurrent chats
 * - Agent selection and management per chatId
 * - Chat session handling per chatId
 * - Stream reader management for real-time responses
 * - Initialization state tracking per chatId
 *
 * @returns {Object} Chat management functions and state
 * @property {Object} chats - Map of chat sessions indexed by chatId
 * @property {Array} copilotAgents - List of available copilot agents with their configurations
 * @property {Object|null} defaultAgent - The default copilot agent (SAGE_COPILOT) from the available agents
 * @property {Function} setInteraction - Update or create a chat interaction with specified chatId and parameters
 * @property {Function} clearChat - Clear all chat interactions for a specific chatId
 * @property {Function} clearAllChats - Clear all chat interactions for all chats
 * @property {Function} setChatSession - Set the current chat session for a specific chatId
 * @property {Function} setIsInitialized - Set initialization status for a specific chatId
 * @property {Function} setActiveAgent - Set the active copilot agent for a specific chatId
 * @property {Function} setCopilotAgents - Set the complete list of available copilot agents
 * @property {Function} refreshAgents - Fetch and refresh the agents list from the API, updating state (currently disabled)
 * @property {Function} getStreamReader - Get the current active stream reader for a specific chatId
 * @property {Function} setStreamReader - Set the active stream reader for a specific chatId
 * @property {Function} getChatState - Get the chat state for a specific chatId (includes tables and reports at chat level). Returns the latest state synchronously, even after dispatch calls.
 * @property {Function} setVote - Set the vote for a specific interaction
 * @property {number} goldenMinuteCtaViews - Number of times the golden minute CTA has been viewed
 * @property {Function} updateGoldenMinuteCtaViews - Increment the golden minute CTA views counter
 * @property {Object} copilotBarProps - State properties for the CopilotBar component
 * @property {Function} setCopilotBarProps - Update CopilotBar component state properties
 */
const useCopilot = () => {
    const { t } = useWeb5Context();
    // Use a ref to store the latest state synchronously - this allows getChatState
    // to return the most recent state even immediately after a dispatch call,
    // before React has re-rendered the component
    const stateRef = useRef(initialState);
    const [state, dispatch] = useReducer((prevState, action) => {
        const newState = copilotReducer(prevState, action);
        // Update ref synchronously when reducer runs
        stateRef.current = newState;
        return newState;
    }, initialState);
    const streamReadersRef = useRef({}); // Track active stream readers per chatId

    /**
     * Updates or creates a chat interaction
     * @param {Object} params - Interaction parameters
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {string} params.id - Unique identifier for the interaction (required)
     * @param {Object} [params.action] - Action object to be added to the interaction's actions array
     * @param {Array} [params.audioBatches] - Array of audio batch objects for voice messages
     * @param {Object} [params.audioData] - Audio data object with batches, waveformData, and audioDuration
     * @param {Date|string} [params.creationDate] - Override the creation date for the interaction
     * @param {boolean} [params.disableVote] - Whether to disable voting for this interaction
     * @param {Object} [params.form] - Form object to be added to the interaction's forms array
     * @param {boolean} [params.isError] - Whether an error occurred
     * @param {boolean} [params.isFinished] - Whether the interaction is complete
     * @param {boolean} [params.isLoading] - Whether the interaction is loading
     * @param {boolean} [params.isQuestion] - Whether this is a user question (determines role)
     * @param {string} [params.parentId] - ID of the parent interaction
     * @param {Object} [params.report] - Report data to be merged into the chat session's reports
     * @param {string} [params.responseId] - ID of the response
     * @param {Object} [params.table] - Table data to be merged into the chat session's tables
     * @param {string} [params.text] - Chat message content
     */
    const setInteraction = useCallback(
        ({
            action,
            artifact,
            artifacts,
            audioBatches,
            audioData,
            chatId,
            creationDate,
            delta,
            disableVote,
            form,
            id,
            isError,
            isFinished,
            isLoading,
            isQuestion,
            parentId,
            report,
            responseId,
            table,
            text,
        } = {}) => {
            dispatch({
                type: ACTIONS.SET_INTERACTION,
                payload: {
                    action,
                    artifact,
                    artifacts,
                    audioBatches,
                    audioData,
                    chatId,
                    creationDate,
                    delta,
                    disableVote,
                    form,
                    id,
                    isError,
                    isFinished,
                    isLoading,
                    isQuestion,
                    parentId,
                    report,
                    responseId,
                    table,
                    text,
                },
            });
        },
        [],
    );

    /**
     * Clears all chat interactions and resets the chat state for a specific chatId
     * Also cancels any ongoing stream reader for the specified chat
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {boolean} [params.skipCancelStreamReader=false] - Whether to skip canceling the stream reader for this chat
     */
    /**
     * Removes a single interaction from a chat
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session
     * @param {string} params.id - Unique identifier of the interaction to remove
     */
    const removeInteraction = useCallback(({ chatId, id } = {}) => {
        dispatch({ type: ACTIONS.REMOVE_INTERACTION, payload: { chatId, id } });
    }, []);

    const clearChat = useCallback(({ chatId, skipCancelStreamReader = false } = {}) => {
        // Cancel and cleanup stream reader for this chat
        const reader = streamReadersRef.current[chatId];
        if (reader && !skipCancelStreamReader) {
            try {
                reader.cancel();
            } catch (error) {
                console.warn('Error canceling stream reader for chatId:', chatId, error);
            }
            delete streamReadersRef.current[chatId];
        }

        dispatch({ type: ACTIONS.CLEAR_CHAT, payload: { chatId } });
    }, []);

    /**
     * Clears all chat interactions for all chats
     * Also cancels all ongoing stream readers
     */
    const clearAllChats = useCallback(() => {
        // Cancel and cleanup all stream readers
        Object.entries(streamReadersRef.current).forEach(([chatId, reader]) => {
            if (reader) {
                try {
                    reader.cancel();
                } catch (error) {
                    console.warn('Error canceling stream reader for chatId:', chatId, error);
                }
            }
        });
        streamReadersRef.current = {};

        dispatch({ type: ACTIONS.CLEAR_ALL_CHATS });
    }, []);

    /**
     * Sets the currently active copilot agent for a specific chatId
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {Object|null} params.agent - The agent object to set as active, or null to clear
     */
    const setActiveAgent = useCallback(({ chatId, agent } = {}) => {
        dispatch({ type: ACTIONS.SET_ACTIVE_AGENT, payload: { chatId, agent } });
    }, []);

    /**
     * Updates the list of available copilot agents
     * @param {Array} agents - Array of agent objects (required)
     */
    const setCopilotAgents = useCallback((agents) => {
        dispatch({ type: ACTIONS.SET_COPILOT_AGENTS, payload: agents });
    }, []);

    /**
     * Sets the chat session for a specific chatId
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {Object} params.session - Session data to set
     */
    const setChatSession = useCallback(({ chatId, session } = {}) => {
        dispatch({ type: ACTIONS.SET_CHAT_SESSION, payload: { chatId, session } });
    }, []);

    /**
     * Sets the initialization status for a specific chatId
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {boolean} params.value - Initialization status
     */
    const setIsInitialized = useCallback(({ chatId, value } = {}) => {
        dispatch({ type: ACTIONS.SET_INITIALIZED, payload: { chatId, value } });
    }, []);

    /**
     * Sets the vote for a specific interaction
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {string} params.interactionId - Unique identifier for the interaction (required)
     * @param {string} params.vote - Vote value to set
     */
    const setVote = useCallback(({ chatId, interactionId, vote } = {}) => {
        dispatch({ type: ACTIONS.SET_VOTE, payload: { chatId, interactionId, vote } });
    }, []);

    /**
     * Gets the chat state for a specific chatId synchronously.
     * Uses stateRef to return the latest state even immediately after a dispatch
     * call before React has re-rendered.
     *
     * Note: This function is stable (empty deps) and should only be used in callbacks
     * where synchronous access to the latest state is needed. For reactive state
     * in useMemo/useEffect, use `chats` directly from the hook return value.
     *
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required for meaningful operation)
     * @returns {Object} Chat state containing chat, chatSession, isInitialized and activeAgent
     */
    const getChatState = useCallback(({ chatId } = {}) => {
        return stateRef.current.chats[chatId] || { ...defaultChatState };
    }, []);

    /**
     * Sets the active stream reader for managing streaming responses for a specific chatId
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required)
     * @param {ReadableStreamDefaultReader|null} params.reader - The stream reader to set as active
     */
    const setStreamReader = useCallback(({ chatId, reader } = {}) => {
        if (!chatId) return;
        if (reader) {
            streamReadersRef.current[chatId] = reader;
        } else {
            delete streamReadersRef.current[chatId];
        }
    }, []);

    /**
     * Gets the current active stream reader for a specific chatId
     * @param {Object} params - Parameters object
     * @param {string} params.chatId - Unique identifier for the chat session (required for meaningful operation)
     * @returns {ReadableStreamDefaultReader|null} The current active stream reader for the chat, or null if none is set
     */
    const getStreamReader = useCallback(({ chatId } = {}) => {
        if (!chatId) return null;
        return streamReadersRef.current[chatId] || null;
    }, []);

    const getDefaultAgent = useCallback(
        (newAgents) => {
            return (newAgents || state.copilotAgents).find(
                (agent) => agent.name === FEATURES.SAGE_COPILOT,
            );
        },
        [state.copilotAgents],
    );

    const defaultAgent = useMemo(() => {
        return getDefaultAgent();
    }, [getDefaultAgent]);

    /**
     * Fetches and updates the agents list
     * Updates active agents for all existing chats if their current agent is no longer available
     * @note Currently disabled - returns early without performing any operations (TODO: Remove when API is ready)
     * @returns {Promise<Array>|undefined} Promise that resolves to the agents data array when enabled, undefined when disabled
     * @throws {Error} When the API call fails or encounters an error (only when enabled)
     */
    const handleGetAgents = useCallback(() => {
        return getAgents()
            .then(({ data }) => {
                dispatch({ type: ACTIONS.SET_COPILOT_AGENTS, payload: data });

                const defaultAgent = getDefaultAgent(data);

                // Update active agents for all existing chats
                Object.keys(state.chats).forEach((chatId) => {
                    const chatState = state.chats[chatId];
                    const currentAgent = chatState.activeAgent;

                    // If chat doesn't have an agent or current agent is no longer available
                    if (!currentAgent || !data.find((agent) => agent.name === currentAgent?.name)) {
                        // Set default agent for this chat
                        if (defaultAgent) {
                            setActiveAgent({ chatId, agent: defaultAgent });
                        }
                    }
                });

                return data;
            })
            .catch((error) => {
                console.error(error);
                errorToast({
                    t,
                    title: t('cfm_label_error_generic'),
                });
                throw error;
            });
    }, [getDefaultAgent, state.chats, setActiveAgent, t]);

    /**
     * Increments the golden minute CTA views counter
     * Used to track how many times the golden minute call-to-action has been viewed
     */
    const updateGoldenMinuteCtaViews = useCallback(() => {
        dispatch({ type: ACTIONS.DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS });
    }, []);

    /**
     * Updates the CopilotBar component state properties
     * @param {Object} props - Properties to update (e.g., { isOpen: true })
     */
    const setCopilotBarProps = useCallback((payload = {}) => {
        dispatch({ type: ACTIONS.SET_COPILOT_BAR_PROPS, payload });
    }, []);

    return {
        chats: state.chats,
        clearAllChats,
        clearChat,
        copilotAgents: state.copilotAgents,
        copilotBarProps: state.copilotBarProps,
        defaultAgent,
        getChatState,
        getStreamReader,
        refreshAgents: handleGetAgents,
        setActiveAgent,
        setChatSession,
        setCopilotAgents,
        removeInteraction,
        setCopilotBarProps,
        setInteraction,
        setIsInitialized,
        setStreamReader,
        setVote,
        goldenMinuteCtaViews: state.goldenMinute.ctaViews,
        updateGoldenMinuteCtaViews,
    };
};

export default useCopilot;
